import { useEffect, useState, useMemo } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import {
  fetchPollResponses,
  invalidatePollResponses,
} from "../lib/pollResponsesCache";

export interface PollVote {
  id: string;
  poll_id: string;
  response_type: string;
  response_data: Record<string, "yes" | "no" | "maybe">;
  respondent_name?: string;
  created_at: string;
}

export interface VoteStats {
  yes: number;
  no: number;
  maybe: number;
}

export function usePollVotes(pollId: string | undefined) {
  const [votes, setVotes] = useState<PollVote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!pollId) {
      setVotes([]);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchVotes = async () => {
      // Only set loading on first fetch if we don't have votes yet
      if (votes.length === 0) setIsLoading(true);

      try {
        // RPC SECURITY DEFINER : lockdown RLS anon (bug hunt 2026-04-23).
        // Remplace `from("poll_responses").select().eq("poll_id")` — la
        // policy permissive sera retirée en étape 1c.
        // ⚡ Passe par le cache d'in-flight pour dédupliquer avec useVoting
        // (cf. pollResponsesCache.ts).
        const { data, error } = await fetchPollResponses(pollId);

        if (error) throw error;

        if (isMounted) {
          setVotes(data as PollVote[]);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching votes:", err);
          setError(
            err instanceof Error
              ? err
              : new Error("Unknown error fetching votes"),
          );
          logger.error("Error fetching votes", "vote", { pollId, error: err });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchVotes();

    // Subscribe to real-time changes
    const channel = supabase
      .channel(`public:poll_responses:poll_id=eq.${pollId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "poll_responses",
          filter: `poll_id=eq.${pollId}`,
        },
        (payload) => {
          logger.info("Real-time vote update received", "vote", {
            event: payload.eventType,
          });
          // Invalider le cache pour forcer un vrai refetch (et non un hit cache)
          invalidatePollResponses(pollId);
          fetchVotes();
        },
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, [pollId]);

  // Derived stats - Optimized with Map for O(1) lookups
  const statsByOption = useMemo(() => {
    const stats = new Map<string, VoteStats>();

    for (const vote of votes) {
      if (!vote.response_data) continue;

      for (const [optionId, value] of Object.entries(vote.response_data)) {
        if (value !== "yes" && value !== "no" && value !== "maybe") continue;

        let stat = stats.get(optionId);
        if (!stat) {
          stat = { yes: 0, no: 0, maybe: 0 };
          stats.set(optionId, stat);
        }
        stat[value]++;
      }
    }

    // Convert Map to Record pour maintenir la compatibilité de l'API
    return Object.fromEntries(stats.entries()) as Record<string, VoteStats>;
  }, [votes]);

  const getStats = (optionId: string): VoteStats => {
    return statsByOption[optionId] || { yes: 0, no: 0, maybe: 0 };
  };

  const getRanking = (type: "all" | "yes" = "all"): Record<string, number> => {
    const scores: Record<string, number> = {};

    // Calculate scores for all options that have stats or default to 0
    // Note: We only know about options that have votes or if passed externally,
    // but here we only calculate based on received votes.
    // The consumer should handle options with no votes (score 0).
    Object.keys(statsByOption).forEach((optionId) => {
      const { yes, no, maybe } = statsByOption[optionId];
      // Simple scoring: Yes = 2, Maybe = 1, No = -1
      scores[optionId] = yes * 2 + maybe * 1 - no * 1;
    });

    // Create sorted list of [optionId, score]
    const sortedOptions = Object.entries(scores).sort(
      ([, scoreA], [, scoreB]) => scoreB - scoreA,
    );

    // Assign ranks
    const ranks: Record<string, number> = {};
    sortedOptions.forEach(([optId], index) => {
      ranks[optId] = index + 1;
    });

    return ranks;
  };

  return {
    votes,
    isLoading,
    error,
    getStats,
    getRanking,
  };
}
